// The project Home's Files gadget: the place to drop new files (Add files) and the latest files the project was given
// (Inputs) and made (Results); a file's name opens it in the editor (the Files view on that file), "All files" opens the
// Files tab. The Brain is its own gadget (BrainGadget below). Dropping works anywhere on the page (DashboardView's drop
// handlers); this card is where it is shown. A viewer lists and downloads only.
import React, { Suspense, lazy, useMemo, useRef, useState } from 'react';
import { useInRouterContext, useLocation, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { DownloadSimple, File as FileIcon, FileImage, FilePdf, FileText, UploadSimple } from '@phosphor-icons/react';
import * as vfsClient from '../../utils/vfsClient';
import { searchForHomeMode } from '../../utils/projectTabUrl';

// The tree is a large module and only shows when the card is opened.
const RepositoryTree = lazy(() => import('../RepositoryTree'));
const RECENT = 5;
const BRAIN_STORE_PREFIX = 'flow.home.files.brain.open.';
const BRAIN_DIR = 'brain';

// Known-folder blurbs, matching the READMEs adk-be-app seeds each folder with; any other folder gets a generic label.
// `inputs` and `outputs` are the Inputs and Results of this card, so the Brain group lists the other folders.
export const FOLDER_DESCRIPTIONS = {
  design: 'Screenshots and design references.',
  docs: "The project's own documentation.",
  memory: "The agent's durable, cross-session notes and learnings about this project.",
  scripts: 'Reusable automation, runnable via vfs_bash.',
};
const FILE_GROUP_FOLDERS = ['inputs', 'outputs'];

const readOpen = (projectId, prefix) => {
  try { return window.localStorage.getItem(`${prefix}${projectId}`) === '1'; } catch (_error) { return false; }
};
const writeOpen = (projectId, open, prefix) => {
  try { window.localStorage.setItem(`${prefix}${projectId}`, open ? '1' : '0'); } catch (_error) { /* the card still works */ }
};

/** "12 KB" for a size in bytes; '' when it is not known. */
export function formatBytes(bytes) {
  if (!Number.isFinite(bytes) || bytes < 0) { return ''; }
  if (bytes < 1024) { return `${bytes} B`; }
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) { value /= 1024; unit += 1; }
  return `${value >= 10 ? Math.round(value) : Math.round(value * 10) / 10} ${units[unit]}`;
}

/** "3 min ago", "2 h ago", "5 d ago" for a time; '' when it is not known. */
export function ageOf(time, now = Date.now()) {
  const at = typeof time === 'number' ? time : Date.parse(time);
  if (!Number.isFinite(at)) { return ''; }
  const seconds = Math.max(0, Math.round((now - at) / 1000));
  if (seconds < 60) { return 'just now'; }
  if (seconds < 3600) { return `${Math.floor(seconds / 60)} min ago`; }
  if (seconds < 86400) { return `${Math.floor(seconds / 3600)} h ago`; }
  return `${Math.floor(seconds / 86400)} d ago`;
}

const iconFor = (name) => {
  const extension = (name.split('.').pop() || '').toLowerCase();
  if (extension === 'pdf') { return FilePdf; }
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'].includes(extension)) { return FileImage; }
  if (['md', 'txt', 'csv', 'json', 'yml', 'yaml', 'doc', 'docx'].includes(extension)) { return FileText; }
  return FileIcon;
};

/** One list of the Inputs then the Results, newest first when the times are known, as `{ name, kind, dir, size, modified }`. */
export function mergeFiles(inputs, outputs, inputsDir, outputsDir) {
  const tag = (entries, kind, dir) => entries.filter((entry) => entry.type !== 'directory').map((entry) => ({
    name: entry.name, kind, dir, size: entry.size, modified: entry.modified,
  }));
  const all = [...tag(outputs, 'Result', outputsDir), ...tag(inputs, 'Input', inputsDir)];
  const time = (file) => { const at = Date.parse(file.modified); return Number.isFinite(at) ? at : 0; };
  return all.map((file, index) => ({ file, index })).sort((a, b) => time(b.file) - time(a.file) || a.index - b.index).map(({ file }) => file);
}

function download(projectId, file, onError) {
  vfsClient.readBinaryFile(projectId, `${file.dir}/${file.name}`).then((blob) => {
    if (!blob) { throw new Error('The file could not be read'); }
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = file.name;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }).catch((error) => onError(error?.message || 'The file could not be downloaded'));
}

function FileRow({ file, projectId, onOpenFile, onError }) {
  const Icon = iconFor(file.name);
  const size = formatBytes(file.size);
  const age = ageOf(file.modified);
  return (
    <li className="ph-file-row" data-testid="home-file-row">
      <Icon aria-hidden className="ph-file-icon" size={18} />
      <button className="ph-link ph-file-name" onClick={() => onOpenFile(`${file.dir}/${file.name}`)} title={`Open ${file.dir}/${file.name}`} type="button">{file.name}</button>
      <span className="ph-file-kind ph-muted ph-small">{file.kind}</span>
      {(size || age) && <span className="ph-file-meta ph-muted ph-small">{[size, age].filter(Boolean).join(' · ')}</span>}
      <button aria-label={`Download ${file.name}`} className="ph-link ph-file-download" onClick={() => download(projectId, file, onError)} title="Download" type="button">
        <DownloadSimple aria-hidden size={16} />
      </button>
    </li>
  );
}

FileRow.propTypes = {
  file: PropTypes.object.isRequired,
  onError: PropTypes.func.isRequired,
  onOpenFile: PropTypes.func.isRequired,
  projectId: PropTypes.string.isRequired,
};


/**
 * `(path) => void`: opens a project file in the editor, the Files view on that file (`?view=files&path=…`, what Canvas
 * reads to open it), its folder focused through `onOpenFiles`. Outside a router (tests) only the folder opens.
 */
export function useOpenFile(onOpenFiles) {
  const inRouter = useInRouterContext();
  // The router context never changes for a mounted component, so the hooks below are called in the same order each render.
  /* eslint-disable react-hooks/rules-of-hooks */
  const navigate = inRouter ? useNavigate() : null;
  const location = inRouter ? useLocation() : null;
  /* eslint-enable react-hooks/rules-of-hooks */
  return (path) => {
    const dir = path.split('/').slice(0, -1).join('/');
    if (navigate && location) {
      const params = new URLSearchParams(searchForHomeMode(location.search, 'files'));
      params.set('path', path);
      params.delete('line');
      navigate({ pathname: location.pathname, search: `?${params.toString()}`, hash: location.hash }, { replace: true });
    }
    onOpenFiles(dir);
  };
}

/** The Brain group: the brain's folders (not Inputs and Results), each a drop target and with its own Add for an editor. */
function BrainGroup({ projectId, folders, canEdit, open, onToggle, onOpenFiles, onUploadTo }) {
  const picker = useRef(null);
  const target = useRef(null);
  const [over, setOver] = useState(null);
  const rows = folders.filter((folder) => folder.type === 'directory' && !FILE_GROUP_FOLDERS.includes(folder.name));
  const takenIn = (folder) => (folder.children || []).map((child) => child.name);
  const drop = (folder) => (event) => {
    setOver(null);
    const files = Array.from(event.dataTransfer?.files || []);
    if (files.length === 0) { return; }
    event.preventDefault();
    onUploadTo(`${BRAIN_DIR}/${folder.name}`, files, takenIn(folder));
  };
  const picked = (event) => {
    const folder = target.current;
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (folder && files.length > 0) { onUploadTo(`${BRAIN_DIR}/${folder.name}`, files, takenIn(folder)); }
  };
  return (
    <section aria-label="Brain" className="ph-card ph-braincard" data-testid="home-brain">
      <div className="ph-card-head">
        <h2 className="ph-card-title">Brain</h2>
        <span className="ph-muted ph-small">The project’s notes, kept apart from the code</span>
      </div>
      {rows.length > 0 && (
        <ul className="ph-list ph-brain-list">
          {rows.map((folder) => (
            <li
              className={`ph-brain-row${over === folder.name ? ' is-over' : ''}`}
              data-brain-drop={canEdit ? folder.name : undefined}
              key={folder.name}
              onDragLeave={canEdit ? () => setOver(null) : undefined}
              onDragOver={canEdit ? (event) => { event.preventDefault(); setOver(folder.name); } : undefined}
              onDrop={canEdit ? drop(folder) : undefined}
            >
              <button className="ph-list-row ph-brain-open" onClick={() => onOpenFiles(`${BRAIN_DIR}/${folder.name}`)} type="button">
                <span className="ph-mono">{folder.name}/</span>
                <span className="ph-muted ph-brain-desc">{FOLDER_DESCRIPTIONS[folder.name] || 'Project folder'}</span>
              </button>
              {canEdit && (
                <button
                  aria-label={`Add files to ${folder.name}`}
                  className="ph-link ph-brain-add"
                  onClick={() => { target.current = folder; picker.current?.click(); }}
                  type="button"
                >
                  <UploadSimple aria-hidden size={14} /> Add
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      <button aria-expanded={open} className="ph-link ph-link-start ph-brain-browse" onClick={onToggle} type="button">
        {open ? 'Hide brain files' : 'Browse all brain files'}
      </button>
      {open && (
        <div className="ph-filescard-tree" data-testid="home-files-tree">
          <Suspense fallback={<div className="ph-muted ph-small">Loading…</div>}>
            <RepositoryTree
              onFileSelect={(path) => onOpenFiles(`${BRAIN_DIR}/${String(path || '').split('/').slice(0, -1).join('/')}`.replace(/\/$/, ''))}
              projectId={projectId}
              readOnly={!canEdit}
              serviceName={BRAIN_DIR}
              vfsBacked
            />
          </Suspense>
        </div>
      )}
      {canEdit && <input data-testid="home-brain-input" hidden multiple onChange={picked} ref={picker} type="file" />}
    </section>
  );
}

BrainGroup.propTypes = {
  canEdit: PropTypes.bool,
  folders: PropTypes.array.isRequired,
  onOpenFiles: PropTypes.func.isRequired,
  onToggle: PropTypes.func.isRequired,
  onUploadTo: PropTypes.func.isRequired,
  open: PropTypes.bool,
  projectId: PropTypes.string.isRequired,
};

export default function FilesSection({
  projectId, canEdit = false, inputs = [], outputs = [], inputsDir, outputsDir, onAddFiles, onOpenFiles, onOpenAll = null, upload = null, dragging = false,
}) {
  const [error, setError] = useState(null);
  const openFile = useOpenFile(onOpenFiles);
  const files = useMemo(() => mergeFiles(inputs, outputs, inputsDir, outputsDir), [inputs, outputs, inputsDir, outputsDir]);
  // A viewer sees the card with files; an editor also before the first one, to add some.
  if (files.length === 0 && !canEdit && !upload) { return null; }
  return (
    <section aria-label="Files" className="ph-card ph-filescard" data-testid="home-files">
      <div className="ph-card-head ph-filescard-head">
        <h2 className="ph-card-title">Files</h2>
        {files.length > 0 && <span className="ph-muted ph-small">{files.length} {files.length === 1 ? 'file' : 'files'}</span>}
        {onOpenAll && <button className="ph-link ph-card-links" onClick={onOpenAll} type="button">All files</button>}
      </div>
      {canEdit && (
        <div className={`ph-dropzone${dragging ? ' is-over' : ''}`} data-testid="home-dropzone">
          <UploadSimple aria-hidden size={20} />
          <span>Drop files here, or anywhere on this page</span>
          <button className="ph-btn ph-btn-small" onClick={onAddFiles} type="button">Add files</button>
        </div>
      )}
      {(upload || error) && (
        <div className="ph-filescard-note">
          {upload}
          {error && <div className="ph-error" role="alert">{error}</div>}
        </div>
      )}
      {files.length > 0 ? (
        <ul className="ph-file-list">
          {files.slice(0, RECENT).map((file) => (
            <FileRow file={file} key={`${file.dir}/${file.name}`} onError={setError} onOpenFile={openFile} projectId={projectId} />
          ))}
        </ul>
      ) : (
        <p className="ph-muted ph-small ph-filescard-empty">No files yet. What you add becomes input for the assistant; what it makes shows as results.</p>
      )}
      {files.length > RECENT && onOpenAll && (
        <button className="ph-link ph-filescard-more" onClick={onOpenAll} type="button">and {files.length - RECENT} more</button>
      )}
    </section>
  );
}

/** The Brain gadget: the brain's folders (each a drop target with its own Add) and its tree; null when it has none. */
export function BrainGadget({ projectId, canEdit = false, folders = [], onOpenFiles, onUploadToBrain = null }) {
  const [open, setOpen] = useState(() => readOpen(projectId, BRAIN_STORE_PREFIX));
  const rows = folders.filter((folder) => folder.type === 'directory' && !FILE_GROUP_FOLDERS.includes(folder.name));
  if (rows.length === 0) { return null; }
  return (
    <BrainGroup
      canEdit={canEdit}
      folders={folders}
      onOpenFiles={onOpenFiles}
      onToggle={() => { const next = !open; setOpen(next); writeOpen(projectId, next, BRAIN_STORE_PREFIX); }}
      onUploadTo={(dir, picked, taken) => onUploadToBrain?.(dir, picked, taken)}
      open={open}
      projectId={projectId}
    />
  );
}

BrainGadget.propTypes = {
  canEdit: PropTypes.bool,
  folders: PropTypes.array,
  onOpenFiles: PropTypes.func.isRequired,
  // (dir, files, takenNames): save files dropped on or added to a brain folder under a free name.
  onUploadToBrain: PropTypes.func,
  projectId: PropTypes.string.isRequired,
};

FilesSection.propTypes = {
  canEdit: PropTypes.bool,
  dragging: PropTypes.bool,
  inputs: PropTypes.array,
  inputsDir: PropTypes.string.isRequired,
  onAddFiles: PropTypes.func.isRequired,
  // Opens the Files tab ("All files", "and N more"); none: those links are left out.
  onOpenAll: PropTypes.func,
  onOpenFiles: PropTypes.func.isRequired,
  outputs: PropTypes.array,
  outputsDir: PropTypes.string.isRequired,
  projectId: PropTypes.string.isRequired,
  // DashboardView's upload note (uploading / error / "added, ask the assistant to read them").
  upload: PropTypes.node,
};
